import { defineStore } from 'pinia'

interface ActiveImpersonation {
  sessionId: string
  targetWorkspaceId: string
  targetWorkspaceName: string | null
  startedAt: string
  expiresAt: string
  hardCeilingAt: string
}

export const useImpersonationStore = defineStore('impersonation', {
  state: () => ({
    isPlatformAdmin: false,
    active: null as ActiveImpersonation | null,
    lastFetchTime: 0,
    isLoading: false
  }),

  actions: {
    async fetchStatus() {
      this.isLoading = true
      try {
        // During SSR, `$fetch` does NOT forward the caller's cookies to
        // internal API routes — so the endpoint sees no user and returns
        // 401. Use `useRequestFetch` on the server so the Supabase auth
        // cookie propagates; fall back to `$fetch` on the client.
        const fetcher = import.meta.server ? useRequestFetch() : $fetch
        const res = await fetcher('/api/admin/platform/impersonate') as {
          isPlatformAdmin: boolean
          active: ActiveImpersonation | null
        }
        this.isPlatformAdmin = res.isPlatformAdmin
        this.active = res.active
        this.lastFetchTime = Date.now()
      } catch {
        this.isPlatformAdmin = false
        this.active = null
      } finally {
        this.isLoading = false
      }
    },

    async startImpersonation(targetWorkspaceId: string) {
      const res = await $fetch<ActiveImpersonation>('/api/admin/platform/impersonate', {
        method: 'POST',
        body: { targetWorkspaceId }
      })
      this.active = res
      // Force-reload so all stores re-fetch against the new workspace.
      if (typeof window !== 'undefined') {
        window.location.href = '/dashboard'
      }
    },

    async stopImpersonation() {
      await $fetch('/api/admin/platform/impersonate', { method: 'DELETE' })
      this.active = null
      if (typeof window !== 'undefined') {
        window.location.href = '/admin'
      }
    }
  }
})
